import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { Observable, Observer } from 'rxjs';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';

import {
  ConfirmationResult,
  deleteUser,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';
import { TranslocoService } from '@ngneat/transloco';

const ALLOW_ANONYMOUS_SIGN_IN = false; // Set to true to allow anonymous sign-in

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private verifier?: RecaptchaVerifier;
  private verifiers?: Array<RecaptchaVerifier> = Array(0); // Array to hold multiple verifiers for different reCAPTCHA instances
  private recaptchaIndex = 0; // Used to track the index of the reCAPTCHA verifier
  constructor(
    private readonly firebaseService: FirebaseService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly translocoService: TranslocoService
  ) {}

  public sendSignInWithEmailLink(email: string): Observable<any | null> {
    return new Observable((observer: Observer<any | null>) => {
      const firebaseAuth = this.firebaseService.auth;
      const currentDomain =
        (window as any).location.origin + '/login' || 'https://popsy.app';
      const actionCodeSettings = {
        // URL with email param and redirectUrl param.
        url:
          currentDomain +
          `?email=${encodeURIComponent(email)}` +
          ((window as any).location.href.includes('buyback')
            ? '&buyback=true'
            : `&custom_url=${encodeURIComponent((window as any).location.href)}`),
        // This must be true.
        handleCodeInApp: true,
        // The domain must be configured in Firebase Hosting and owned by the project.
        linkDomain: 'popsy.app',
      };
      if (firebaseAuth && this.akitaRouterQuery.isBrowser) {
        sendSignInLinkToEmail(firebaseAuth, email, actionCodeSettings)
          .then((result: any) => {
            observer.next(result);
            observer.complete();
          })
          .catch((error: unknown) => {
            observer.error(parseApiError(error));
            observer.complete();
          });
      } else {
        observer.error(
          parseApiError({
            name: 'FIREBASE_NOT_AVAILABLE',
            code: 'FIREBASE_NOT_AVAILABLE',
            message: 'Firebase is not available on SSR or Firebase failed to load',
          })
        );
        observer.complete();
      }
    });
  }

  public confirmSignInWithEmailLink(email: string): Observable<any | null> {
    return new Observable((observer: Observer<any | null>) => {
      const firebaseAuth = this.firebaseService.auth;
      if (firebaseAuth && this.akitaRouterQuery.isBrowser) {
        const auth = this.firebaseService.auth;

        const emailLink = (window as any).location.href;
        if (isSignInWithEmailLink(auth, emailLink)) {
          signInWithEmailLink(auth, email, window.location.href)
            .then((result: any) => {
              observer.next(result);
              observer.complete();
            })
            .catch((error: unknown) => {
              observer.error(parseApiError(error));
              observer.complete();
            });
        } else {
          observer.error(
            parseApiError({
              name: 'INVALID_EMAIL_LINK',
              code: 'INVALID_EMAIL_LINK',
              message: 'Invalid email link',
            })
          );
          observer.complete();
        }
      } else {
        observer.error(
          parseApiError({
            name: 'FIREBASE_NOT_AVAILABLE',
            code: 'FIREBASE_NOT_AVAILABLE',
            message: 'Firebase is not available on SSR or Firebase failed to load',
          })
        );
        observer.complete();
      }
    });
  }

  public refreshAuth(): void {
    this.firebaseService.refreshAuth();
  }

  // this one is used in the sms-sign-in component
  public signInWithPhoneNumber(
    phoneNumber: string,
    retryOTP: boolean
  ): Observable<ConfirmationResult> {
    return new Observable((observer) => {
      void (async () => {
        try {
          const auth = this.firebaseService.auth;
          if (!auth) {
            throw new Error('Firebase Auth not initialized');
          }

          // Sanitize phone number: remove spaces
          const sanitizedPhoneNumber = phoneNumber.replace(/\s+/g, '');

          if (retryOTP) {
            this.recaptchaIndex++;
          }

          // Create a container for reCAPTCHA if not already created
          const recaptchaContainer = document.getElementById(
            'recaptchaContainer' + `${this.recaptchaIndex}`
          ) as HTMLElement;

          // Set up verifier if not already created
          // if (!this.verifiers) {
          this.verifiers = Array(0);
          this.verifiers[this.recaptchaIndex] = new RecaptchaVerifier(
            recaptchaContainer,
            { size: 'invisible' },
            auth
          );
          const confirmationResult = await signInWithPhoneNumber(
            this.firebaseService.getFirebaseAuth(),
            sanitizedPhoneNumber,
            this.verifiers[this.recaptchaIndex]
          );

          observer.next(confirmationResult);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();

      return () => {
        // Cleanup: clear the reCAPTCHA widget
        this.verifier?.clear();
        this.verifier = undefined;
      };
    });
  }

  public confirmOtp(
    confirmationResult: ConfirmationResult | null,
    otpCode: string
  ): Observable<UserCredential> {
    return new Observable((observer) => {
      confirmationResult
        ?.confirm(otpCode)
        .then((userCredential: UserCredential) => {
          observer.next(userCredential);
          observer.complete();
        })
        .catch((error: any) => {
          observer.error(error);
        });
    });
  }

  public clearVerifier(): void {
    this.verifier?.clear();
    this.verifier = undefined;
  }

  public signInWithGoogle(): Observable<any> {
    return new Observable((observer) => {
      const provider = new GoogleAuthProvider();

      signInWithPopup(this.firebaseService.auth, provider)
        .then((result) => {
          observer.next(result.user);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error with Google Sign-In:', error);
          observer.error(parseApiError(error));
        });
    });
  }

  public signInWithApple(): Observable<any> {
    return new Observable((observer) => {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      provider.setCustomParameters({
        locale: this.translocoService.getActiveLang(),
      });

      signInWithPopup(this.firebaseService.auth, provider)
        .then((result) => {
          // The signed-in user info
          observer.next(result);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error with Apple Sign-In:', error);
          observer.error(parseApiError(error));
        });
    });
  }

  public sigInAnonymous(): Observable<any> {
    return new Observable((observer) => {
      void (async () => {
        if (!ALLOW_ANONYMOUS_SIGN_IN) {
          observer.next(null);
          observer.complete();
        }

        try {
          const auth = this.firebaseService.getFirebaseAuth();
          if (!auth) {
            throw new Error('Firebase Auth not initialized');
          }

          signInAnonymously(auth)
            .then((userCredential: UserCredential) => {
              observer.next(userCredential);
              observer.complete();
            })
            .catch((error: any) => {
              observer.error(error);
            });
        } catch (error) {
          observer.error(parseApiError(error));
        }
      })();
    });
  }

  public signOut(): Observable<void> {
    return new Observable((observer) => {
      void (async () => {
        try {
          const auth = this.firebaseService.getFirebaseAuth();
          if (!auth) {
            throw new Error('Firebase Auth not initialized');
          }

          await signOut(auth);

          // Optional: reset verifier if needed
          this.clearVerifier();

          observer.next();
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }

  public deleteAccount(): Observable<void> {
    return new Observable((observer) => {
      void (async () => {
        try {
          const auth = this.firebaseService.getFirebaseAuth();
          if (!auth) {
            throw new Error('Firebase Auth not initialized');
          }

          const user = auth.currentUser;
          if (!user) {
            throw new Error('No user is currently signed in');
          }

          await deleteUser(user);
          observer.next();
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }
}
